//store/index.ts
import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import localForage from 'localforage';
import { encryptTransform } from 'redux-persist-transform-encrypt';

import { LightTokenGetterApi } from '../infrastructure/Common/LightTokenGetterApi';
import { securityReducer, doSecurityRefreshToken } from './useSecurityStore';
import { clientReducer } from './useClientStore';
import shoppingCart from './useShoppingCartStore';
import promos from './usePromosStore';
import shoppingCartAnonSession from './useShoppingCartAnonSessionStore';
import tenantMetadata from './useTenantMetadataStore';
import carouselCache from './useCarouselCacheStore';
import { googleAnalyticsReducer } from './useGoogleAnalytics';
import anon from './useAnonStore';
import { tenantSettingReducer } from './useTenantSettingStore';

import config from '../config';
import { sessionMiddleware } from './sessionMiddleware';
import { authEvents } from '../infrastructure/Common/authEvents';
import { doSecurityLogout } from './useSecurityStore';
import Logger from '../infrastructure/Logging/Logger';
import globalUrls from '../globalUrls';

// Lock localForage to IndexedDB for best at-rest protection in browsers
localForage.config({
  name: `${config.tenant}DB`,
  storeName: `${config.tenant}_fortress`,
  description: `${config.tenant} app state`,
});

const secureTransform = encryptTransform({
  secretKey: config.stateSecret,
  onError: (error) => {
    Logger.error(`Persist encryption error: ${error}`);
  },
});

const securityPersistConfig = {
  key: 'security',
  storage: localForage,
  version: 1,
  whitelist: [
    'isLoggedIn',
    'roles',
    'token',
    'userSession',
  ],
  transforms: [secureTransform],
};

const clientPersistConfig = {
  key: 'client',
  storage: localForage,
  version: 1,
  whitelist: ['entity', 'selectedAddressId'], // include checkout selections
  transforms: [secureTransform],
};

const anonSessionPersistConfig = {
  key: 'shoppingCartAnonSession',
  storage: localForage,
  version: 1,
  whitelist: ['sessionId', 'shoppingCartId', 'expiresAt'],
  transforms: [secureTransform],
};

// Keep a lightweight, non-encrypted cache of carousel content for fast render
const carouselCachePersistConfig = {
  key: 'carouselCache',
  storage: localForage,
  version: 1,
  whitelist: ['items', 'order', 'rehydrateTimestamp'],
};

// Persist anonymous session id (non-sensitive)
const anonPersistConfig = {
  key: 'anon',
  storage: localForage,
  version: 1,
  whitelist: ['anonSessionId', 'distritoId'],
};

const rootReducer = combineReducers({
  tenantMetadata,
  tenantSetting: tenantSettingReducer,
  carouselCache: persistReducer(carouselCachePersistConfig, carouselCache),
  security: persistReducer(securityPersistConfig, securityReducer),
  client: persistReducer(clientPersistConfig, clientReducer),
  shoppingCart,
  shoppingCartAnonSession: persistReducer(anonSessionPersistConfig, shoppingCartAnonSession),
  promos,
  anon: persistReducer(anonPersistConfig, anon),
  googleAnalytics: googleAnalyticsReducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefault) =>
    getDefault({
      serializableCheck: {
        ignoredActions: [
          'persist/PERSIST',
          'persist/REHYDRATE',
          'persist/FLUSH',
          'persist/PAUSE',
          'persist/PURGE',
          'persist/REGISTER',
        ],
      },
    }).concat(sessionMiddleware),
});

// 🔌 Bridge: let infra read/refresh auth without importing the store
LightTokenGetterApi.configure({
  getToken: () => store.getState().security.token ?? null,
  refresh: async () => { await store.dispatch(doSecurityRefreshToken()).unwrap(); },
});

export const persistor = persistStore(store);

// Subscribe once: when infra reports session expiration, perform logout cleanup via store
(() => {
  let handled = false;
  authEvents.on('sessionExpired', () => {
    if (handled) return;
    handled = true;
    // fire and forget; middleware will clear slices
    store.dispatch(doSecurityLogout());
    // redirect user to login page
    try {
      if (typeof window !== 'undefined' && window?.location) {
        window.location.assign(globalUrls.CLIENT_LOGIN);
      }
    } catch {
      console.error('Error navigating to login page after session expiration');
    }
  });

  // If user logs in again, allow future sessionExpired events to trigger logout
  let prevIsLoggedIn = store.getState().security.isLoggedIn;
  store.subscribe(() => {
    const isLoggedIn = store.getState().security.isLoggedIn;
    if (isLoggedIn && !prevIsLoggedIn) {
      handled = false;
    }
    prevIsLoggedIn = isLoggedIn;
  });
})();

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;